/**
 * Builder Box Sizes
 *
 * The box picked in the box step sets how many items the next step(s) can
 * hold, and hides products that are too big for it.
 *
 *   Box product tag      "Items: 4-6"           (spaces and en dashes are fine)
 *   Optional box tag     "Box: Mini"            (else the name is found in the title)
 *   Product tag          "Too big for: Mini, Gift Bag"  ("To big for: ..." also works)
 *
 * Settings (Theme settings > Builder Box Sizes) arrive as window.builderBoxConfig.
 * "steps" is "1>2": the box chosen in app step 1 limits app step 2. Several
 * limited steps: "1>2,3".
 *
 * What it does on the builder page:
 *   - a note on the limited step: "Your Mini box holds 4–6 items. You have 3."
 *   - at the limit, "Add to bundle" and "+" on that step are refused
 *   - moving past the limited step, or adding the bundle to the cart, is refused
 *     while the count is outside the box's range (e.g. after switching to a
 *     smaller box) or while a chosen item is too big for the box
 *   - products tagged too big for the chosen box are hidden in later steps
 *
 * The app's markup is never rewritten; clicks are refused in the capture
 * phase on window, before the app and the theme's other builder scripts.
 * DOM writes only happen when a value changes (the app re-renders on every
 * DOM change).
 */
(function () {
  'use strict';

  var cfg = window.builderBoxConfig;
  if (!cfg || window.builderBoxSize) return;

  var SEL = {
    root: '.bogos-bp-products-info',
    tab: '.bogos-bp-steps-container .bogos-bp-step-item',
    next: '.bogos-bp-step-next-btn',
    stepsBar: '.bogos-bp-steps-bar',
    card: '.bogos-bp-product-card',
    addProduct: '.bogos-bp-btn-add-product',
    addBundle: '.bogos-bp-button-add',
    summaryStep: '.bogos-step-items-container',
    summaryItem: '.bogos-step-item',
    summaryTitle: '.bogos-step-item-title',
    summaryPlus: '.bogos-plus-step-item-btn',
    qty: '.bogos-bp-quantity-input'
  };
  var CACHE_KEY = 'builderBoxSize.v1';
  var TAG_TTL = 5 * 60 * 1000; // re-read tags after 5 minutes so new tags show up

  var NAMES = String(cfg.names || '').split(',')
    .map(function (s) { return s.trim(); }).filter(Boolean)
    .sort(function (a, b) { return b.length - a.length; }); // longest first: "Gift Bag" before "Bag"

  var BOX_STEP = 1, LIMITED = [2];
  (function () {
    var m = /^\s*(\d+)\s*>\s*([\d,\s]+)$/.exec(String(cfg.steps || ''));
    if (!m) return;
    BOX_STEP = parseInt(m[1], 10);
    LIMITED = m[2].split(',').map(function (n) { return parseInt(n, 10); }).filter(function (n) { return n > 0; });
  })();

  var MSG = {
    note: cfg.note || 'Your {box} holds {min}–{max} items. You have {n}.',
    full: cfg.full || 'Your {box} is full ({max} items). Remove an item to add another.',
    under: cfg.under || 'Your {box} needs at least {min} items. Please add {missing} more.',
    over: cfg.over || 'Your {box} holds up to {max} items. Please remove {extra}.',
    tooBig: cfg.tooBig || "{item} doesn't fit in the {box}. Please remove it or pick a bigger box."
  };

  // ------------------------------------------------------------- product data
  // handle -> { tags: [lowercase], at: time }, variant id -> handle
  var store = readCache();
  var products = store.products || {};
  var variants = store.variants || {};
  var pending = {};

  function readCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  var saveTimer = null;
  function saveCache() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ products: products, variants: variants })); } catch (e) { /* ignore */ }
    }, 300);
  }

  function tagsOf(handle) {
    var p = products[handle];
    if (p && Date.now() - p.at < TAG_TTL) return p.tags;
    // builder-filter.js may already have them
    var bf = window.builderFilter && window.builderFilter.tags && window.builderFilter.tags[handle];
    if (bf && !p) {
      bf = bf.map(function (t) { return String(t).toLowerCase().trim(); });
      products[handle] = { tags: bf, at: Date.now() };
      return bf;
    }
    fetchTags(handle);
    return p ? p.tags : null; // stale beats nothing while the fresh copy loads
  }

  var queue = [], running = 0;
  function fetchTags(handle) {
    if (!handle || pending[handle]) return;
    pending[handle] = true;
    queue.push(handle);
    pump();
  }
  function pump() {
    while (running < 3 && queue.length) {
      running++;
      (function (h) {
        fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then(function (p) {
            products[h] = { tags: (p.tags || []).map(function (t) { return String(t).toLowerCase().trim(); }), title: p.title, at: Date.now() };
            (p.variants || []).forEach(function (v) { variants[v.id] = h; });
            saveCache();
          })
          .catch(function () { products[h] = products[h] || { tags: [], at: Date.now() - TAG_TTL + 60000 }; })
          .then(function () { delete pending[h]; running--; pump(); update(); });
      })(queue.shift());
    }
  }

  // Remember which product each variant belongs to, from the cards on screen.
  function learnCards() {
    document.querySelectorAll(SEL.addProduct + '[data-variant-id][data-product-handle]').forEach(function (b) {
      var v = b.getAttribute('data-variant-id');
      if (!variants[v]) { variants[v] = b.getAttribute('data-product-handle'); saveCache(); }
    });
    document.querySelectorAll(SEL.card + ' [data-variant-id]').forEach(function (el) {
      var card = el.closest(SEL.card);
      var h = card && card.getAttribute('data-product-handle');
      var v = el.getAttribute('data-variant-id');
      if (h && v && !variants[v]) { variants[v] = h; saveCache(); }
    });
  }

  // ------------------------------------------------------------ box details
  function parseRange(tags) {
    for (var i = 0; i < tags.length; i++) {
      var m = /^items\s*:\s*(\d+)\s*(?:[-–—]|to)?\s*(\d+)?\s*$/.exec(tags[i]);
      if (m) {
        var a = parseInt(m[1], 10), b = m[2] ? parseInt(m[2], 10) : a;
        return { min: Math.min(a, b), max: Math.max(a, b) };
      }
    }
    return null;
  }

  function boxName(tags, title) {
    for (var i = 0; i < tags.length; i++) {
      var m = /^box\s*:\s*(.+)$/.exec(tags[i]);
      if (m) {
        var t = m[1].trim();
        for (var j = 0; j < NAMES.length; j++) if (NAMES[j].toLowerCase() === t) return NAMES[j];
        return t.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      }
    }
    var low = String(title || '').toLowerCase();
    for (var k = 0; k < NAMES.length; k++) if (low.indexOf(NAMES[k].toLowerCase()) !== -1) return NAMES[k];
    return '';
  }

  function tooBigFor(tags) {
    var out = [];
    tags.forEach(function (t) {
      var m = /^too? big for\s*:\s*(.+)$/.exec(t); // "To big for" is also used in the store
      if (m) m[1].split(',').forEach(function (n) { n = n.trim(); if (n) out.push(n); });
    });
    return out;
  }

  function doesNotFit(tags, name) {
    if (!tags || !name) return false;
    var n = name.toLowerCase();
    return tooBigFor(tags).some(function (x) { return x === n || x.indexOf(n) !== -1 || n.indexOf(x) !== -1; });
  }

  // ----------------------------------------------------------- bundle state
  function summaryBox(step) {
    return document.querySelector(SEL.summaryStep + '[data-step-id="' + step + '"]') ||
      document.querySelectorAll(SEL.summaryStep)[step - 1] || null;
  }

  function itemsOf(step) {
    var box = summaryBox(step);
    if (!box) return [];
    return Array.prototype.map.call(box.querySelectorAll(SEL.summaryItem), function (el) {
      var q = el.querySelector(SEL.qty);
      var n = q ? parseInt(q.value, 10) : 1;
      var t = el.querySelector(SEL.summaryTitle);
      return { variant: el.getAttribute('data-variant-id'), title: t ? t.textContent.trim() : '', qty: isNaN(n) ? 1 : n };
    });
  }

  function countOf(step) {
    return itemsOf(step).reduce(function (s, it) { return s + it.qty; }, 0);
  }

  // The chosen box: { name, min, max, title } or null (no box, or no "Items:" tag yet).
  function chosenBox() {
    var it = itemsOf(BOX_STEP)[0];
    if (!it) return null;
    var h = variants[it.variant];
    if (!h) return null;
    var tags = tagsOf(h);
    if (!tags) return null;
    var range = parseRange(tags);
    if (!range) return null;
    return { name: boxName(tags, it.title) || it.title, min: range.min, max: range.max, title: it.title };
  }

  function currentStep() {
    if (document.querySelector('.bf-occ-active')) return 0; // Kids occasion step (builder-filter.js)
    var t = document.querySelectorAll(SEL.tab);
    for (var i = 0; i < t.length; i++) {
      if (t[i].classList.contains('active') || t[i].getAttribute('aria-current') === 'step') return i + 1;
    }
    return 0;
  }

  function fill(text, box, extra) {
    // "Mini" reads as "Mini box"; names that already say what they are ("Gift Bag") stay as they are.
    var label = /\b(box|bag|basket|tote|crate|tin)$/i.test(box.name) ? box.name : box.name + ' box';
    var vals = { box: label, min: box.min, max: box.max };
    for (var k in extra) vals[k] = extra[k];
    return text.replace(/\{(\w+)\}/g, function (m, k) { return vals[k] !== undefined ? vals[k] : m; });
  }

  // First problem that should stop the shopper, for steps up to (excluding) `before`.
  function problem(box, before) {
    if (!box) return null;
    for (var i = 0; i < LIMITED.length; i++) {
      var s = LIMITED[i];
      if (s >= before) continue;
      var n = countOf(s);
      if (n < box.min) return { step: s, text: fill(MSG.under, box, { missing: box.min - n, n: n }) };
      if (n > box.max) return { step: s, text: fill(MSG.over, box, { extra: n - box.max, n: n }) };
    }
    var steps = document.querySelectorAll(SEL.summaryStep).length;
    for (var st = BOX_STEP + 1; st <= steps; st++) {
      if (st >= before) break;
      var items = itemsOf(st);
      for (var j = 0; j < items.length; j++) {
        var h = variants[items[j].variant];
        if (h && doesNotFit(tagsOf(h), box.name)) return { step: st, text: fill(MSG.tooBig, box, { item: items[j].title }) };
      }
    }
    return null;
  }

  // ------------------------------------------------------------------ UI
  // One wrapper right after the steps bar holds the note and the message.
  var wrap = document.createElement('div');
  wrap.className = 'bf-box-wrap';
  var noteEl = document.createElement('div');
  noteEl.className = 'bf-box-note';
  noteEl.setAttribute('aria-live', 'polite');
  noteEl.hidden = true;
  var msgEl = document.createElement('div');
  msgEl.className = 'bf-box-msg';
  msgEl.setAttribute('role', 'alert');
  msgEl.hidden = true;
  wrap.appendChild(noteEl);
  wrap.appendChild(msgEl);
  var hideTimer = null;

  function place() {
    var bar = document.querySelector(SEL.stepsBar);
    if (!bar || !bar.parentNode) return false;
    if (wrap.previousSibling !== bar) bar.parentNode.insertBefore(wrap, bar.nextSibling);
    return true;
  }

  function setText(el, text) { if (el.textContent !== text) el.textContent = text; }
  function setShown(el, on) { if (el.hidden === on) el.hidden = !on; }

  function showMessage(text) {
    if (!place()) return;
    setText(msgEl, text);
    setShown(msgEl, true);
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () { setShown(msgEl, false); }, 6000);
  }

  function update() {
    if (!document.querySelector(SEL.root)) return;
    learnCards();
    var box = chosenBox();
    var step = currentStep();
    var limited = box && LIMITED.indexOf(step) !== -1;

    // Note on the limited step
    if (limited && place()) {
      var n = countOf(step);
      setText(noteEl, fill(MSG.note, box, { n: n }));
      var state = n > box.max ? 'over' : n >= box.max ? 'full' : n >= box.min ? 'ok' : 'under';
      if (noteEl.getAttribute('data-state') !== state) noteEl.setAttribute('data-state', state);
      setShown(noteEl, true);
    } else {
      setShown(noteEl, false);
    }
    var full = !!(limited && countOf(step) >= box.max);
    if (document.body.classList.contains('bf-box-full') !== full) document.body.classList.toggle('bf-box-full', full);

    // Hide products that don't fit, on steps after the box step
    var hideOn = box && step > BOX_STEP;
    document.querySelectorAll(SEL.card).forEach(function (card) {
      var h = card.getAttribute('data-product-handle');
      var hide = !!(hideOn && h && doesNotFit(tagsOf(h), box.name));
      if (card.classList.contains('bf-toobig') !== hide) card.classList.toggle('bf-toobig', hide);
    });
  }

  // ---------------------------------------------------------------- clicks
  function block(e, text) {
    e.stopImmediatePropagation();
    e.preventDefault();
    showMessage(text);
  }

  function targetStep(t) {
    if (t.closest(SEL.next)) return currentStep() + 1;
    var tab = t.closest(SEL.tab);
    if (!tab) return -1;
    return Array.prototype.indexOf.call(document.querySelectorAll(SEL.tab), tab) + 1;
  }

  function onClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var box = chosenBox();
    if (!box) return;
    var step = currentStep();

    // Adding to a limited step that is already full
    var adding = t.closest(SEL.addProduct) || (t.closest(SEL.card) && t.closest('[class*="plus"]'));
    if (adding && LIMITED.indexOf(step) !== -1 && countOf(step) >= box.max) {
      return block(e, fill(MSG.full, box, { n: countOf(step) }));
    }
    var plus = t.closest(SEL.summaryPlus);
    if (plus) {
      var sb = plus.closest(SEL.summaryStep);
      var all = Array.prototype.slice.call(document.querySelectorAll(SEL.summaryStep));
      var s = sb ? (parseInt(sb.getAttribute('data-step-id'), 10) || all.indexOf(sb) + 1) : 0;
      if (LIMITED.indexOf(s) !== -1 && countOf(s) >= box.max) return block(e, fill(MSG.full, box, { n: countOf(s) }));
    }

    // Finishing the bundle
    if (t.closest(SEL.addBundle)) {
      var p = problem(box, Infinity);
      if (p) return block(e, p.text);
      return;
    }

    // Moving forward past a limited step
    var target = targetStep(t);
    if (target > step) {
      var q = problem(box, target);
      if (q) block(e, q.text);
    }
  }

  function onKey(e) {
    if (e.key === 'Enter' || e.key === ' ') onClick(e);
  }

  function injectStyles() {
    var css = document.createElement('style');
    css.textContent =
      '.bf-toobig{display:none !important}' +
      '.bf-box-note{margin:10px 0 0;padding:8px 12px;border-radius:6px;background:#f6f0e7;color:#333;font-size:14px;text-align:center}' +
      '.bf-box-note[data-state="full"]{background:#eef3e8}' +
      '.bf-box-note[data-state="over"]{background:#fdecea;color:#8a1f11}' +
      '.bf-box-note[hidden],.bf-box-msg[hidden]{display:none}' +
      '.bf-box-msg{margin:10px 0 0;padding:8px 12px;border-radius:6px;background:#fff6e5;color:#7a5a12;font-size:14px;text-align:center}' +
      '.bf-box-full .bogos-bp-products-info .bogos-bp-btn-add-product{opacity:.45;cursor:not-allowed}';
    document.head.appendChild(css);
  }

  function init() {
    window.builderBoxSize = {
      chosenBox: chosenBox, countOf: countOf, problem: problem,
      products: products, variants: variants, steps: { box: BOX_STEP, limited: LIMITED }
    };
    injectStyles();
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey, true);
    // Cheap check twice a second: everything it writes is guarded, and it also
    // catches quantity changes the app makes without re-rendering.
    setInterval(update, 500);
    update();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
